import React from 'react';
import { FiCpu, FiBookOpen, FiArrowRight } from 'react-icons/fi';

interface HealthRecommendationsPanelProps {
  /** Abre o modal de treinamento (Sprint UI-2) */
  onTrainAgent: () => void;
  /** Navega pra tab "Lacunas" (Sprint UI-3) */
  onViewGaps: () => void;
}

/**
 * Painel de CTAs que conecta a tab Saúde com os próximos passos do
 * workflow: treinar o agente (Sprint UI-2 — `AgentTrainModal`) e
 * resolver lacunas de conhecimento (Sprint UI-3 — tab Lacunas).
 *
 * Os callbacks são propagados desde o `AgentFormPage` através do
 * `HealthTab`, dando acesso ao state `showTrainModal` e `setActiveTab`.
 */
export const HealthRecommendationsPanel: React.FC<
  HealthRecommendationsPanelProps
> = ({ onTrainAgent, onViewGaps }) => {
  return (
    <div
      className="rounded-lg border p-5"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <h3
        className="text-base font-semibold mb-4"
        style={{ color: 'var(--theme-card-text)' }}
      >
        Próximos passos
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RecommendationButton
          icon={<FiCpu className="h-5 w-5" />}
          title="Treinar agente"
          description="Rode testes controlados e receba sugestões de melhoria do prompt."
          onClick={onTrainAgent}
        />
        <RecommendationButton
          icon={<FiBookOpen className="h-5 w-5" />}
          title="Ver lacunas de conhecimento"
          description="Descubra perguntas que seu agente não sabe responder e complete a base."
          onClick={onViewGaps}
        />
      </div>
    </div>
  );
};

interface RecommendationButtonProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

const RecommendationButton: React.FC<RecommendationButtonProps> = ({
  icon,
  title,
  description,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-lg border p-4 transition-colors flex items-start gap-3 group"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--theme-primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--theme-card-border)';
      }}
    >
      <div
        className="flex-shrink-0 p-2 rounded-md"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          color: 'var(--theme-primary)',
        }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="flex items-center gap-2 mb-1"
          style={{ color: 'var(--theme-card-text)' }}
        >
          <span className="text-sm font-semibold">{title}</span>
          <FiArrowRight
            className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ color: 'var(--theme-primary)' }}
          />
        </div>
        <p
          className="text-xs leading-relaxed"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {description}
        </p>
      </div>
    </button>
  );
};
